import { ProjectWithRelations } from "@/hooks/useProjects";
import { cn } from "@/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { format, parseISO } from "date-fns";

interface CalendarProjectCellProps {
  project: ProjectWithRelations;
  onClick?: () => void;
  isStart?: boolean;
  isEnd?: boolean;
  spanHeight?: number;
  leftOffset?: number | string;
  topOffset?: number;
  width?: number | string;
  barStartDay?: number;
  eventStartDay?: number | null;
  eventEndDay?: number | null;
  isEventStart?: boolean;
  isEventEnd?: boolean;
  rowHeight?: number;
}

const getStatusColor = (status: string | null) => {
  switch (status) {
    case "confirmed":
      return "bg-[hsl(var(--status-confirmed))]";
    case "offer":
      return "bg-[hsl(var(--status-offer))]";
    case "rejected":
      return "bg-[hsl(var(--status-rejected))]";
    case "initial_contact":
    default:
      return "bg-[hsl(var(--status-initial))]";
  }
};

const getTypeColor = (types: string[] | null) => {
  if (!types || types.length === 0) return "bg-muted-foreground";

  const primaryType = types[0];
  switch (primaryType) {
    case "live":
      return "bg-[hsl(var(--type-live))]";
    case "film":
      return "bg-[hsl(var(--type-film))]";
    case "gfx_tracking":
      return "bg-[hsl(var(--type-gfx))]";
    case "consultancy":
      return "bg-[hsl(var(--type-consultancy))]";
    default:
      return "bg-muted-foreground";
  }
};

/** Calendar color of a project: its type when confirmed, else its status */
export function getProjectColorClass(project: {
  status: string | null;
  types: string[] | null;
}) {
  return project.status === "confirmed"
    ? getTypeColor(project.types)
    : getStatusColor(project.status);
}

export function CalendarProjectCell({
  project,
  onClick,
  isStart,
  isEnd,
  spanHeight = 20,
  leftOffset = 0,
  topOffset = 0,
  width = 46,
  barStartDay = 1,
  eventStartDay = null,
  eventEndDay = null,
  isEventStart = false,
  isEventEnd = false,
  rowHeight = 24,
}: CalendarProjectCellProps) {
  const colorClass = getProjectColorClass(project);

  const eventStartDate = project.event_start_date || project.event_end_date;
  const eventEndDate = project.event_end_date || project.event_start_date;
  const planningStartDate =
    project.planning_start_date || eventStartDate || null;
  const planningEndDate = project.planning_end_date || eventEndDate || null;
  const hasPlanningRange = !!(
    project.planning_start_date || project.planning_end_date
  );
  const hasEventSubRange = eventStartDay !== null && eventEndDay !== null;

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return null;
    return format(parseISO(dateStr), "dd.MM.yyyy");
  };

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          onClick={onClick}
          className={cn(
            "absolute px-1 text-[9px] font-medium text-white cursor-pointer transition-opacity hover:opacity-80 z-10 overflow-hidden",
            isStart && "rounded-t",
            isEnd && "rounded-b",
            !isStart && !isEnd && "rounded-none",
            isStart && isEnd && "rounded",
          )}
          style={{
            height: `${spanHeight}px`,
            left:
              typeof leftOffset === "number" ? `${leftOffset}px` : leftOffset,
            top: `${topOffset}px`,
            width: typeof width === "number" ? `${width}px` : width,
          }}
        >
          <div
            className={cn(
              "absolute inset-0",
              colorClass,
              hasPlanningRange && "opacity-60",
            )}
          />
          {/* Event sub-range overlay at full opacity */}
          {hasEventSubRange && (
            <div
              className={cn(
                "absolute left-0 right-0",
                colorClass,
                isEventStart && "rounded-t",
                isEventEnd && "rounded-b",
              )}
              style={{
                top: `${(eventStartDay - barStartDay) * rowHeight}px`,
                height: `${(eventEndDay - eventStartDay + 1) * rowHeight - 4}px`,
              }}
            />
          )}
          <span className="relative block break-words leading-tight">
            {project.name}
          </span>
        </button>
      </TooltipTrigger>
      <TooltipContent side="right" className="max-w-xs">
        <div className="space-y-1">
          <p className="font-semibold">{project.name}</p>
          {project.companies?.name && (
            <p className="text-xs text-muted-foreground">
              {project.companies.name}
            </p>
          )}
          <div className="flex items-center gap-2 text-xs">
            <span className="text-muted-foreground">Event:</span>
            {eventStartDate && <span>{formatDate(eventStartDate)}</span>}
            {eventStartDate &&
              eventEndDate &&
              eventStartDate !== eventEndDate && <span>→</span>}
            {eventEndDate && eventStartDate !== eventEndDate && (
              <span>{formatDate(eventEndDate)}</span>
            )}
            {!eventStartDate && !eventEndDate && (
              <span className="text-muted-foreground">Not set</span>
            )}
          </div>
          {(project.planning_start_date || project.planning_end_date) && (
            <div className="flex items-center gap-2 text-xs">
              <span className="text-muted-foreground">Planning:</span>
              {planningStartDate && (
                <span>{formatDate(planningStartDate)}</span>
              )}
              {planningStartDate &&
                planningEndDate &&
                planningStartDate !== planningEndDate && <span>→</span>}
              {planningEndDate && planningStartDate !== planningEndDate && (
                <span>{formatDate(planningEndDate)}</span>
              )}
            </div>
          )}
          {project.status && (
            <span
              className={cn(
                "inline-block px-1.5 py-0.5 rounded text-[10px] capitalize",
                project.status === "confirmed" && "status-confirmed",
                project.status === "offer" && "status-offer",
                project.status === "initial_contact" &&
                  "status-initial-contact",
                project.status === "rejected" && "status-rejected",
              )}
            >
              {project.status.replace("_", " ")}
            </span>
          )}
        </div>
      </TooltipContent>
    </Tooltip>
  );
}
